import { a as isPotentialPHIField, c as sanitizeMetadata, i as hashUserId, l as validateEventName, n as SAFE_NAME_TERMS, o as isPotentialPHIValue, r as createSafeMetadata, s as sanitizeErrorMessage, t as DEFAULT_PHI_FIELDS } from "./sanitizer-CLnKD_rP.js";
import { n as StatsigSettingsContext, r as useStatsigSettings, t as useTrackEvent } from "./useTrackEvent-IE9Pu1uh.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { StatsigProvider as StatsigProvider$1, useDynamicConfig, useExperiment, useLayer, useStatsigClient, useStatsigClient as useStatsigClient$1, useStatsigUser, useStatsigUser as useStatsigUser$1 } from "@statsig/react-bindings";
import { Fragment, jsx } from "react/jsx-runtime";

//#region src/components/StatsigProvider.tsx
const ANONYMOUS_USER = {};
/**
* Inner component that exposes the initialized client and propagates debug settings
* to the settings context that hooks read from.
*/
const StatsigClientBridge = ({ debugLogEvents, onClientReady, children }) => {
	const { client } = useStatsigClient$1();
	useEffect(() => {
		if (onClientReady) onClientReady(client);
	}, [client, onClientReady]);
	const settings = useMemo(() => ({ debugLogEvents }), [debugLogEvents]);
	return /* @__PURE__ */ jsx(StatsigSettingsContext.Provider, {
		value: settings,
		children
	});
};
/**
* Statsig provider wrapper.
*
* Plugins (auto-capture, session replay) must be constructed by the consumer
* and passed via the `plugins` prop — Statsig binds them only at client
* construction on first render. Use the helpers in `@jotpsych/statsig/plugins`
* to get pre-wired PHI-safe instances.
*
* @example
* import { StatsigProvider } from '@jotpsych/statsig';
* import {
*   createSanitizedAutoCapturePlugin,
*   createTriggeredSessionReplayPlugin,
* } from '@jotpsych/statsig/plugins';
*
* const plugins = useMemo(
*   () => [
*     createSanitizedAutoCapturePlugin(),
*     createTriggeredSessionReplayPlugin(),
*   ],
*   [],
* );
*
* <StatsigProvider
*   sdkKey={import.meta.env.VITE_STATSIG_SDK_KEY}
*   user={{ custom: { surface: 'web-app' } }}
*   environment={import.meta.env.MODE}
*   debugLogEvents={import.meta.env.DEV}
*   plugins={plugins}
*   enableLocalOverrides={import.meta.env.DEV}
*   onClientReady={(client) => bridgeClient(client)}
* >
*   <App />
* </StatsigProvider>
*/
const StatsigProvider = ({ sdkKey, user, environment = "production", debugLogEvents = false, plugins, enableLocalOverrides = false, onClientReady, children }) => {
	const [overrideAdapter, setOverrideAdapter] = useState(void 0);
	useEffect(() => {
		if (!enableLocalOverrides || !sdkKey) return;
		import(
			/* @vite-ignore */
			["@statsig", "js-local-overrides"].join("/")
).then((mod) => {
			const adapter = new mod.LocalOverrideAdapter(sdkKey);
			setOverrideAdapter(adapter);
			window.statsigOverrides = adapter;
			window.statsigDebug = {
				showOverrides: () => adapter.getAllOverrides(),
				showStorage: () => {
					Object.keys(localStorage).filter((k) => k.includes("local-overrides")).forEach((k) => console.log(k, localStorage.getItem(k)));
				}
			};
		}).catch((error) => {
			console.warn("[Statsig] Local overrides enabled but @statsig/js-local-overrides is not installed. Skipping.", error);
		});
	}, [enableLocalOverrides, sdkKey]);
	const options = useMemo(() => ({
		environment: { tier: environment },
		overrideAdapter,
		plugins
	}), [
		plugins,
		overrideAdapter,
		environment
	]);
	if (!sdkKey) {
		console.warn("[Statsig] No SDK key provided; rendering children without Statsig context.");
		return /* @__PURE__ */ jsx(Fragment, { children });
	}
	return /* @__PURE__ */ jsx(StatsigProvider$1, {
		sdkKey,
		user: user ?? ANONYMOUS_USER,
		options,
		children: /* @__PURE__ */ jsx(StatsigClientBridge, {
			debugLogEvents,
			onClientReady,
			children
		})
	});
};

//#endregion
//#region src/hooks/useFeatureGate.ts
/**
* Check a single Statsig feature gate.
*
* The result is memoized to avoid duplicate exposure logs on re-renders.
* Statsig automatically logs an exposure event the first time a gate is checked.
*
* @example
* const showNewUi = useFeatureGate('feature_new_ui');
* return showNewUi ? <NewUi /> : <OldUi />;
*/
function useFeatureGate(gateName) {
	const { checkGate } = useStatsigClient$1();
	return useMemo(() => {
		return checkGate(gateName);
	}, [checkGate, gateName]);
}
/**
* Check multiple Statsig feature gates at once. Each gate logs its own exposure.
*
* @example
* const gates = useFeatureGates(['feature_a', 'feature_b']);
* if (gates.feature_a && gates.feature_b) { ... }
*/
function useFeatureGates(gateNames) {
	const { checkGate } = useStatsigClient$1();
	const gateNamesKey = useMemo(() => JSON.stringify(gateNames), [gateNames]);
	return useMemo(() => {
		return JSON.parse(gateNamesKey).reduce((acc, gateName) => {
			acc[gateName] = checkGate(gateName);
			return acc;
		}, {});
	}, [checkGate, gateNamesKey]);
}

//#endregion
//#region src/hooks/useStatsigUserSync.ts
/**
* Sync a consumer-owned Statsig user into the client and gate rendering on
* first-sync completion.
*
* Must be called inside a `<StatsigProvider>` from `@jotpsych/statsig`.
*
* The hook owns the parts that are easy to get subtly wrong:
* - **Stable-hash memoization** — re-syncs only when the user's content
*   actually changes, not on every reference churn from the consumer's user
*   store.
* - **Ready gate** — holds `isReady: false` until the first `updateUserAsync`
*   settles, so `useFeatureGate` never evaluates against anonymous context
*   and then flips underneath rendered children.
* - **Fail-open on error** — sync failures still flip `isReady: true` so a
*   Statsig outage doesn't block the whole app.
* - **Anonymous fast-path** — `isAuthenticated: false` marks ready instantly.
*
* @example
* const user = useMyAppStatsigUser();
* const { isReady } = useStatsigUserSync({ user, isAuthenticated });
* return isReady ? <App /> : <Spinner />;
*/
function useStatsigUserSync({ user, isAuthenticated }) {
	const [isReady, setIsReady] = useState(false);
	const [error, setError] = useState(null);
	const { updateUserAsync } = useStatsigUser$1();
	const previousHashRef = useRef(null);
	const userHash = useMemo(() => {
		if (!user) return null;
		return JSON.stringify(user);
	}, [user]);
	useEffect(() => {
		if (!isAuthenticated) {
			previousHashRef.current = null;
			setIsReady(true);
			return;
		}
		if (!userHash || !user) return;
		if (userHash === previousHashRef.current) {
			setIsReady(true);
			return;
		}
		let cancelled = false;
		updateUserAsync(user).then(() => {
			if (cancelled) return;
			previousHashRef.current = userHash;
			setError(null);
			setIsReady(true);
		}).catch((e) => {
			if (cancelled) return;
			previousHashRef.current = userHash;
			setError(e instanceof Error ? e : new Error(String(e)));
			setIsReady(true);
		});
		return () => {
			cancelled = true;
		};
	}, [
		isAuthenticated,
		userHash,
		user,
		updateUserAsync
	]);
	return {
		isReady,
		error
	};
}

//#endregion
export { DEFAULT_PHI_FIELDS, SAFE_NAME_TERMS, StatsigProvider, StatsigSettingsContext, createSafeMetadata, hashUserId, isPotentialPHIField, isPotentialPHIValue, sanitizeErrorMessage, sanitizeMetadata, useDynamicConfig, useExperiment, useFeatureGate, useFeatureGates, useLayer, useStatsigClient, useStatsigSettings, useStatsigUser, useStatsigUserSync, useTrackEvent, validateEventName };
//# sourceMappingURL=index.js.map